import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { orIlike, pageRange, searchTerms } from '@/lib/list-query'
import type { Database } from '@/types/database'

type ContactInsert = Database['public']['Tables']['contacts']['Insert']
type ContactUpdate = Database['public']['Tables']['contacts']['Update']
type ContactType = Database['public']['Enums']['contact_type']

const CONTACTS_KEY = ['contacts'] as const

// Only what the list/picker/detail-tab surfaces render — not select('*').
const CONTACT_LIST_COLUMNS =
  'id, account_id, division_id, first_name, last_name, title, email, contact_type, is_active, updated_at, account:accounts(id,name), division:account_divisions(id,name)'

// Picker/name-map consumers (command palette, tasks) and the account-detail
// contacts tab. Unpaginated by design; the contacts list uses useContactsList.
// `accountId === undefined` legitimately means "all contacts", so callers
// that only want account-scoped results must pass `enabled` themselves.
export function useContacts(
  accountId?: string,
  showInactive = false,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    enabled,
    queryKey: accountId
      ? [...CONTACTS_KEY, { accountId, showInactive }]
      : [...CONTACTS_KEY, { showInactive }],
    queryFn: async () => {
      let query = supabase
        .from('contacts')
        .select(CONTACT_LIST_COLUMNS)
        .order('last_name')
      if (accountId) query = query.eq('account_id', accountId)
      if (!showInactive) query = query.eq('is_active', true)
      const { data, error } = await query
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
}

// Whitelist of server-sort keys → PostgREST order columns. 'last_name' gets a
// first_name secondary sort to match the old client accessor (last + first);
// 'account_name' orders parent rows by the embedded account's name.
const CONTACT_SORT_COLUMNS: Record<string, string> = {
  last_name: 'last_name',
  updated_at: 'updated_at',
  account_name: 'account(name)',
}

export interface ContactListParams {
  pageIndex: number
  pageSize: number
  sortBy: string
  sortDir: 'asc' | 'desc'
  showInactive: boolean
  accountId?: string
  divisionId?: string
  contactType?: ContactType
  hasEmail?: boolean
  search?: string
}

export function useContactsList(params: ContactListParams) {
  return useQuery({
    queryKey: [...CONTACTS_KEY, 'list', params],
    queryFn: async () => {
      const { from, to } = pageRange(params.pageIndex, params.pageSize)
      let query = supabase
        .from('contacts')
        .select(CONTACT_LIST_COLUMNS, { count: 'exact' })
      if (!params.showInactive) query = query.eq('is_active', true)
      if (params.accountId) query = query.eq('account_id', params.accountId)
      if (params.divisionId) query = query.eq('division_id', params.divisionId)
      if (params.contactType) query = query.eq('contact_type', params.contactType)
      if (params.hasEmail) query = query.not('email', 'is', null).neq('email', '')
      if (params.search) {
        for (const term of searchTerms(params.search)) {
          query = query.or(orIlike(['first_name', 'last_name', 'email'], term))
        }
      }
      const ascending = params.sortDir === 'asc'
      const sortBy = CONTACT_SORT_COLUMNS[params.sortBy] ?? 'last_name'
      query = query.order(sortBy, { ascending })
      if (sortBy === 'last_name') query = query.order('first_name', { ascending })
      const { data, error, count } = await query
        // stable tie-break so rows can't straddle page boundaries
        .order('id', { ascending: true })
        .range(from, to)
      if (error) throw error
      return { rows: data, total: count ?? 0 }
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

export function useContact(id: string) {
  return useQuery({
    queryKey: [...CONTACTS_KEY, id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contacts')
        .select(
          '*, account:accounts(id,name), division:account_divisions(id,name)'
        )
        .eq('id', id)
        .single()
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
}

export function useCreateContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['contacts', 'create'],
    mutationFn: async (values: ContactInsert) => {
      const { data, error } = await supabase
        .from('contacts')
        .insert(values)
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CONTACTS_KEY })
      toast.success('Contact created')
    },
    onError: (err: Error) => {
      const pgErr = err as unknown as { code?: string }
      if (pgErr.code === '23505') return // constraint errors handled inline by the form
      toast.error(err.message)
    },
  })
}

export function useUpdateContact(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['contacts', 'update'],
    mutationFn: async (values: ContactUpdate) => {
      const { error } = await supabase
        .from('contacts')
        .update(values)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CONTACTS_KEY })
      void qc.invalidateQueries({ queryKey: [...CONTACTS_KEY, id] })
    },
    onError: (err: Error) => toast.error(err.message),
  })
}
